import './Menu.scss';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { auth } from '../../js/GRP';
import { useMiwiStore, generalStore, userStore } from '../../store';
import Helpers from '../../js/helpers';
import Server from '../../js/config/_server';
import {
    COOKIE_KEYS,
    ROUTES,
} from '../../js/_constants';
import Config from '../../js/config/_global';
import Logo from '../logo/Logo';
import Languages from '../languages/Languages';

interface DeviceRouteType {
    pathname: string;
    search: string;
}

interface Item {
    identifier: string;
    route: string | DeviceRouteType;
    text: string;
    icon: string;
}

const Menu = () => {
    const { t } = useTranslation();
    const { setAuthenticated } = generalStore();
    const { resetUser, details, dealerships} = userStore();
    const { miwiLogout } = useMiwiStore();

    const MENU_ITEMS: Item[] = [
        {
            identifier: 'dashboard',
            icon: 'dashboard',
            text: t('dashboard_link'),
            route: ROUTES.DASHBOARD,
        },
        {
            identifier: 'media',
            icon: 'media',
            text: t('media_link'),
            route: ROUTES.MEDIA_LIBRARY,
        },
        {
            identifier: 'playlists',
            icon: 'playlists',
            text: t('playlist_link'),
            route: ROUTES.PLAYLISTS,
        },
        {
            identifier: 'dealerships',
            icon: details.dealer && dealerships.list.length === 1 ? 'devices' : 'dealerships',
            text: details.dealer && dealerships.list.length === 1 ? t('device_link'):  t('dealerships_link'),
            route: ROUTES.DEALERSHIPS,
        },
        {
            identifier: 'welcome-screen',
            icon: 'welcome-screen',
            text: "Welcome Screen",
            route: ROUTES.WELCOME_SCREEN_PAGE,
        },
        // {
        //     identifier: 'devices',
        //     text: t('device_link'),
        //     route: {
        //         pathname: ROUTES.DEVICES,
        //         search: queryString.stringify({
        //             page: 1,
        //             deviceClass: DeviceClassOptionsMap.All
        //         })
        //     },
        // },
        // {
        //     identifier: 'onboarding',
        //     text: t('onboarding_link'),
        //     route: ROUTES.ONBOARDING,
        // },        
    ];

    const [showLanguages, setShowLanguages] = useState<boolean>(false);
  
    const refLanguageIcon = useRef<HTMLLIElement | null>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (refLanguageIcon.current && !refLanguageIcon.current.contains(event.target as Node)) {
                setShowLanguages(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const logout = () => {
        Helpers.removeCookie(COOKIE_KEYS.TOKEN);
        Helpers.removeCookie(COOKIE_KEYS.REFRESH_TOKEN);
        Helpers.removeCookie(COOKIE_KEYS.ID_TOKEN);
        setAuthenticated(false);
        resetUser();
        miwiLogout();
        auth.logout();
    };

    return (
        <div className="b-menu">

            <Logo classNames="small" />

            <div className="pages-links-wrapper">

                <ul>
                    {MENU_ITEMS
                        .filter(item => item.identifier !== 'welcome-screen' || details.isWelcomeScreenEnabled)
                        .map((item, index) => (
                        <li
                            key={index}
                            className={classNames('b-link-with-icon views white', {
                                border: location.pathname.includes(item.identifier)
                            })}
                        >
                            <Link
                                to={item.route}
                                className={item.identifier}
                                data-testid={`${item.identifier}-link`}
                            >
                                <span
                                    className={`${item.icon}-icon`}
                                />
                                <span className="text">
                                    {item.text}
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>

                <div>
                    <a
                        className="bottom-link"
                        rel="noopener noreferrer"
                        target="_blank"
                        href={Server.avpPortal}
                        data-testid='avp-portal-link'
                    >
                        {t('avp_portal')}
                        <span> &gt; </span>
                    </a>
                    <a
                        className="bottom-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={Server.avpImprint}
                        data-testid='imprint-link'
                    >
                        {t('imprint')}
                        <span> &gt; </span>
                    </a>
                    <a
                        className="bottom-link"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={Server.avpLegal}
                        data-testid='legal-link'
                    >
                        {t('legal')}
                        <span> &gt; </span>
                    </a>
                    <Link
                        className="bottom-link"
                        onClick={() => logout()}
                        to={ROUTES.NO_AUTHENTICATED}
                        data-testid='logout-button'
                    >
                        {t('logout_action')}
                        <span> &gt; </span>
                    </Link>
                </div>
            </div>

            <div>            
                <ul className="bottom-links-wrapper">
                    <li className="b-link-with-icon white">
                        <Link to={ROUTES.PROFILE} className="me" data-testid='profile-button'>
                            <span className="me-icon" />
                        </Link>
                    </li>
                    <li className="b-link-with-icon white">
                        <Link to={ROUTES.SUPPORT} className="support" data-testid='support-button'>
                            <span className="support-icon" />
                        </Link>
                    </li>

                    <li
                        ref={refLanguageIcon}                    
                        className="b-link-with-icon white language-icon"
                    >
                        {showLanguages && (
                            <Languages setShowLanguages={setShowLanguages} />
                        )}
                        <button
                            className="language-button"
                            onClick={() => setShowLanguages(!showLanguages)}
                            data-testid='language-button'
                        >
                            <span className="glob-icon" />
                        </button>
                    </li>
                </ul>

                <p className='version'>
                    Retail Content Manager {Config.version} - Internal System
                </p>
            </div>

        </div>
    );
};

export default Menu;
